import React, { useState } from "react";
import RankInsignia from "../../shared/rating/RankInsignia";
import { insigniaForValue } from "../../shared/rating/tiers";
import { wormColors } from "core/src/core/constants.js";
import s from "./FfaBannerLab.module.scss";

// The 6 FFA match-found grid, ten designs to compare, desktop and phone. The
// current one is a thin strip (a 22px badge, 18px name, 44px tall); MS Battle's
// carries an avatar block, big type and a large rank badge with the tier name.
// We have no avatars: the worm's colour and head take that job. Linked from
// the Admin page.

type P = { id: string; name: string; colorId: string; flag: string; rating: number | null };
const PLAYERS: P[] = [
  { id: "you", name: "Mathias", colorId: "blue", flag: "se", rating: 1480 },
  { id: "o1", name: "ravenBlaze", colorId: "pink", flag: "pt", rating: 1320 },
  { id: "o2", name: "Pixel", colorId: "green", flag: "pl", rating: null },
  { id: "o3", name: "Jonas73", colorId: "yellow", flag: "au", rating: 1610 },
  { id: "o4", name: "novaFrost", colorId: "purple", flag: "fr", rating: 1275 },
  { id: "o5", name: "Milo33", colorId: "orange", flag: "nl", rating: 1902 },
];
const DESIGNS = [
  { id: "1", name: "Head block", blurb: "MS Battle's slot, our way: a 46px block with the worm's head in its colour where the avatar sits, a 22px name with the flag, a 40px rank badge with the tier name. 72px tall, the colour down the left." },
  { id: "2", name: "Colour slab", blurb: "The left third is a solid slab in the worm's colour, slanted like the 1v1 banner, holding the flag; the rest dark with the name and rank." },
  { id: "3", name: "Starting grid", blurb: "The race start: rows staggered like a grid, a big lane number 1 to 6 ghosted behind each slot, the colour as a stripe." },
  { id: "4", name: "Badge first", blurb: "The rank badge leads, 48px on the left, the name and tier after it, the flag at the end; the worm's colour as a glowing bar under the slot." },
  { id: "5", name: "Neon frame", blurb: "The slot outlined in the worm's colour with the boxes' neon glow, dark inside; the name large, the badge and tier right." },
  { id: "6", name: "Two-tone", blurb: "Split on a diagonal: the worm's colour with the name in white on the left, dark with the badge and tier on the right." },
  { id: "7", name: "Tall card", blurb: "88px cards, two lines: the name and flag above, the tier below in the rank's colour, a big badge on the right; the colour as a bar across the top." },
  { id: "8", name: "Seat numbers", blurb: "P1 to P6 in the worm's colour leading each slot, Orbitron, the name and rank after, a thin colour underline." },
  { id: "9", name: "Around the mark", blurb: "Three and three around a centre mark, 6 FFA, with the arena's shape behind it; the slots angled toward the middle." },
  { id: "10", name: "Glass", blurb: "Frosted slots over the arena, a soft glow in the worm's colour behind the head, big type; the least boxy." },
];

// Two-tone, varied. All on one base: a dark band behind the grid (the arena's
// walls never sit behind a colour), the colour block and the dark block the
// same width in every slot, the badge in a fixed column at the far right with
// the tier before it, so all six badges line up.
const TWO_TONE = [
  { id: "A", name: "Slant", blurb: "The colour block, 55%, slanted, carries the name in white; the dark block carries the flag and, at the far right, the badge." },
  { id: "B", name: "Straight cut", blurb: "The same with a straight vertical cut and rounded corners." },
  { id: "C", name: "Chevron", blurb: "The colour block ends in a chevron pointing at the flag and badge." },
  { id: "D", name: "Colour right", blurb: "Reversed: the dark block first with the flag and name; the colour block on the right carries the badge on a dark disc." },
  { id: "E", name: "Fade", blurb: "No hard edge: the colour fades to dark across the slot; the name over the colour, the flag and badge over the dark end." },
  { id: "F", name: "Frame", blurb: "The slot in the colour with a dark inset panel that holds everything: flag, name, badge; the colour is the frame." },
  { id: "G", name: "Thin slab", blurb: "A narrower colour block, 40%, big name; more dark for the flag and a larger badge." },
  { id: "H", name: "Badge on the seam", blurb: "The badge on a dark disc on the seam between the blocks; the flag at the far right of the dark." },
];

// Colour slab, varied. The same base: the band behind, a slab of the worm's
// colour at a fixed width holding the flag, the name, then the tier and the
// badge in its column at the far right.
const SLAB = [
  { id: "A", name: "Plain slab", blurb: "A slab of the colour alone, 72px, slanted; the flag and name on the dark after it, the badge right." },
  { id: "B", name: "Head in the slab", blurb: "The slab holds the worm's head on a dark tile; the flag and name on the dark, the badge right." },
  { id: "C", name: "Seat in the slab", blurb: "The slab holds the seat number P1 to P6 in white, Orbitron; the flag and name on the dark, the badge right." },
  { id: "D", name: "Straight", blurb: "A plain slab of the colour with a straight edge, rounded on the outside; the flag and name on the dark, the badge right." },
  { id: "E", name: "Chevron", blurb: "The plain slab ending in a chevron pointing at the name." },
  { id: "F", name: "Bookends", blurb: "The head slab left and a thin cap of the colour at the far right after the badge." },
  { id: "G", name: "Slab right", blurb: "Reversed: the flag and name first on the dark, the badge, then the head slab at the far right slanted the other way." },
  { id: "H", name: "Badge in the slab", blurb: "The slab holds the rank badge on a dark disc; the flag and name on the dark, the seat number at the far right." },
  { id: "I", name: "Straight, bordered", blurb: "2D with the card's border in the player's colour too, and a soft glow of it." },
  { id: "J", name: "Chevron, bordered", blurb: "2E with the card's border in the player's colour too, and a soft glow of it." },
  { id: "K", name: "Straight, badge block", blurb: "2D with the badge in its own block at the end: full height, a shade lighter than the card, split off by a line, as the start page's ranked strip does it." },
  { id: "L", name: "Chevron, badge block", blurb: "2E with the badge block at the end." },
  { id: "M", name: "Straight, bordered, badge block", blurb: "2I (the colour on the border) with the badge block at the end." },
  { id: "N", name: "Chevron, bordered, badge block", blurb: "2J (the colour on the border) with the badge block at the end." },
];

// Head block, varied. A plain disc of the worm's colour (no tile around it, no
// glow), the name and flag, and the badge alone, no tier name; one of them
// keeps the tier to compare.
const HEADS = [
  { id: "A", name: "Disc", blurb: "A 22px disc of the colour, the name and flag, the badge alone at the right; the colour down the left edge too." },
  { id: "B", name: "Disc, with the tier", blurb: "1A with the tier name beside the badge, to compare." },
  { id: "C", name: "Bigger disc, no edge", blurb: "A 30px disc carries the colour alone; no coloured edge." },
  { id: "D", name: "Ring", blurb: "The disc hollow: a 3px ring of the colour." },
  { id: "E", name: "Worm", blurb: "The head with a short trail: a rounded stroke of the colour ending in the disc." },
  { id: "F", name: "Disc right", blurb: "The name and flag first; the disc sits just before the badge." },
  { id: "G", name: "Edge only", blurb: "No disc: a thick 8px edge of the colour, the name and flag, the badge." },
  { id: "H", name: "Tinted", blurb: "The disc, and the colour as a faint tint across the card's left third." },
  { id: "I", name: "Coloured name", blurb: "The disc, and the name in the colour itself." },
  { id: "J", name: "Disc, badge block", blurb: "The disc, and the badge in its own lighter block at the end, the strip's way." },
  { id: "K", name: "Tinted, disc, badge block", blurb: "1H's tint and disc with the badge block at the end, and no highlight on your own card: every card the same." },
  { id: "L", name: "Tinted, no edge", blurb: "1K without the coloured left edge: the tint and the disc carry the colour alone." },
  { id: "M", name: "Badge block, flag after the disc", blurb: "1J with the flag in its own column right after the disc, before the name, so all six flags line up." },
  { id: "N", name: "Badge block, flag before the block", blurb: "1J with the flag right-aligned just before the badge block, a column of its own." },
  { id: "O", name: "Badge block, flag in the block", blurb: "1J with the flag inside the badge block, above the badge; the block carries the player's standing and country together." },
  { id: "P", name: "Badge block, flag under the name", blurb: "1J with two lines: the name above, the flag below it, aligned to the name's left." },
  { id: "Q", name: "Badge block, flag first", blurb: "1J with the flag as the first column, before the disc." },
  { id: "R", name: "Tinted, flag after the disc", blurb: "1K (the tint, the disc, the badge block, no highlight on your card) with the flag in its own column right after the disc." },
  { id: "S", name: "Tinted, flag before the block", blurb: "1K with the flag right-aligned just before the badge block." },
  { id: "T", name: "Tinted, flag in the block", blurb: "1K with the flag inside the badge block, above the badge." },
  { id: "U", name: "Tinted, flag under the name", blurb: "1K with two lines: the name above, the flag below it." },
  { id: "V", name: "Tinted, flag first", blurb: "1K with the flag as the first column, before the disc." },
];

function Head({ color, size = 46 }: { color: string; size?: number }) {
  // the worm's head: a disc in its colour with the trail's glow, on a dark tile
  return (
    <span className={s.head} style={{ width: size, height: size, "--worm": color } as React.CSSProperties}>
      <i style={{ width: size * 0.42, height: size * 0.42 }} />
    </span>
  );
}
function Flag({ code, size = 22 }: { code: string; size?: number }) {
  return <img className={s.flag} style={{ width: size, height: Math.round(size * 0.72) }} src={`https://flagcdn.com/w40/${code}.png`} alt="" />;
}
function Rank({ p, size, tier = true }: { p: P; size: number; tier?: boolean }) {
  const t = p.rating != null ? insigniaForValue(p.rating) : null;
  return (
    <span className={s.rank} style={t ? { color: t.tier.color } : undefined}>
      {p.rating != null ? <RankInsignia value={p.rating} size={size} /> : <RankInsignia rating={null} placeholder size={size} />}
      {tier && <span className={s.tier}>{t ? t.label : "Placements"}</span>}
    </span>
  );
}

function Slot({ d, p, i, phone }: { d: string; p: P; i: number; phone: boolean }) {
  const color = wormColors[p.colorId];
  const you = p.id === "you";
  const st = { "--worm": color, "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties;
  const cls = `${s.slot} ${s["d" + d]} ${you ? s.you : ""} ${phone ? s.slotPhone : ""}`;
  const big = phone ? 30 : 40;
  switch (d) {
    case "1": return <div className={cls} style={st}><Head color={color} size={phone ? 34 : 46} /><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><Rank p={p} size={big} /></div>;
    case "2": return <div className={cls} style={st}><span className={s.slab}><Flag code={p.flag} size={phone ? 22 : 28} /></span><span className={s.name}>{p.name}</span><Rank p={p} size={big} /></div>;
    case "3": return <div className={cls} style={st}><b className={s.lane}>{i + 1}</b><Head color={color} size={phone ? 30 : 40} /><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><Rank p={p} size={big} /></div>;
    case "4": return <div className={cls} style={st}><Rank p={p} size={phone ? 34 : 48} tier={false} /><span className={s.name}>{p.name}<small>{p.rating != null ? insigniaForValue(p.rating).label : "Placements"}</small></span><Flag code={p.flag} size={phone ? 18 : 24} /></div>;
    case "5": return <div className={cls} style={st}><Flag code={p.flag} size={phone ? 18 : 24} /><span className={s.name}>{p.name}</span><Rank p={p} size={big} /></div>;
    case "6": return <div className={cls} style={st}><span className={s.tone}><Flag code={p.flag} size={phone ? 16 : 20} /><span className={s.name}>{p.name}</span></span><Rank p={p} size={big} /></div>;
    case "7": return <div className={cls} style={st}><span className={s.lines}><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><span className={s.sub} style={{ color: p.rating != null ? insigniaForValue(p.rating).tier.color : undefined }}>{p.rating != null ? insigniaForValue(p.rating).label : "Placements"}</span></span><Rank p={p} size={phone ? 36 : 52} tier={false} /></div>;
    case "8": return <div className={cls} style={st}><b className={s.pn}>P{i + 1}</b><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><Rank p={p} size={big} /></div>;
    case "9": return <div className={cls} style={st}><Head color={color} size={phone ? 30 : 40} /><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><Rank p={p} size={phone ? 28 : 36} /></div>;
    default: return <div className={cls} style={st}><Head color={color} size={phone ? 34 : 44} /><span className={s.name}>{p.name} <Flag code={p.flag} size={phone ? 16 : 20} /></span><Rank p={p} size={big} /></div>;
  }
}

function Grid({ d, phone }: { d: string; phone: boolean }) {
  const slots = PLAYERS.map((p, i) => <Slot key={p.id} d={d} p={p} i={i} phone={phone} />);
  if (d === "9") return (
    <div className={`${s.around} ${phone ? s.aroundPhone : ""}`}>
      <div className={s.colL}>{slots.slice(0, 3)}</div>
      <div className={s.mark}><b>6</b><span>FFA</span></div>
      <div className={s.colR}>{slots.slice(3)}</div>
    </div>
  );
  return <div className={`${s.grid} ${s["g" + d]} ${phone ? s.gridPhone : ""}`}>{slots}</div>;
}

function Badge({ p, size }: { p: P; size: number }) {
  return p.rating != null ? <RankInsignia value={p.rating} size={size} /> : <RankInsignia rating={null} placeholder size={size} />;
}
function TT({ v, p, phone }: { v: string; p: P; phone: boolean }) {
  const color = wormColors[p.colorId];
  const you = p.id === "you";
  const big = phone ? 30 : v === "G" ? 46 : 40;
  const badge = <span className={s.ttBadge}><Badge p={p} size={big} /></span>;
  const cls = `${s.tt} ${s["tt" + v]} ${you ? s.ttYou : ""} ${phone ? s.ttPhone : ""}`;
  const st = { "--worm": color } as React.CSSProperties;
  const name = <span className={s.ttName}>{p.name}</span>;
  const flag = <Flag code={p.flag} size={phone ? 20 : 26} />;
  if (v === "D") return <div className={cls} style={st}><span className={s.ttDark}>{flag}{name}</span><span className={s.ttColor}><span className={s.ttDisc}><Badge p={p} size={big} /></span></span></div>;
  if (v === "F") return <div className={cls} style={st}><span className={s.ttDark}>{flag}{name}{badge}</span></div>;
  if (v === "H") return <div className={cls} style={st}><span className={s.ttColor}>{name}</span><span className={s.ttDisc}><Badge p={p} size={big} /></span><span className={s.ttDark}>{flag}</span></div>;
  return <div className={cls} style={st}><span className={s.ttColor}>{name}</span><span className={s.ttDark}>{flag}{badge}</span></div>;
}
function TTGrid({ v, phone }: { v: string; phone: boolean }) {
  return (
    <>
      <div className={`${s.band} ${phone ? s.bandPhone : ""}`} />
      <div className={`${s.grid} ${phone ? s.gridPhone : ""}`}>{PLAYERS.map((p, i) => <div key={p.id} className={s.slot} style={{ "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties}><TT v={v} p={p} phone={phone} /></div>)}</div>
    </>
  );
}
function CS({ v, p, i, phone }: { v: string; p: P; i: number; phone: boolean }) {
  const color = wormColors[p.colorId];
  const you = p.id === "you";
  const big = phone ? 30 : 40;
  const badge = <span className={s.ttBadge}><Badge p={p} size={big} /></span>;
  const cls = `${s.cs} ${s["cs" + v]} ${you ? s.ttYou : ""} ${phone ? s.csPhone : ""}`;
  const st = { "--worm": color } as React.CSSProperties;
  const who = <span className={s.csWho}><Flag code={p.flag} size={phone ? 20 : 26} /><span className={s.csName}>{p.name}</span></span>;
  const head = <Head color={color} size={phone ? 30 : 40} />;
  const seat = <b className={s.csSeat}>P{i + 1}</b>;
  const plain = "ADEIJKLMN".includes(v);
  const slabInner = plain ? null : v === "C" ? seat : v === "H" ? <span className={s.ttDisc}><Badge p={p} size={big} /></span> : head;
  if ("KLMN".includes(v)) return <div className={cls} style={st}><span className={s.csSlab} /><span className={s.csBody}>{who}</span><span className={s.csBlock}><Badge p={p} size={big} /></span></div>;
  if (v === "G") return <div className={cls} style={st}><span className={s.csBody}>{who}{badge}</span><span className={s.csSlab}>{head}</span></div>;
  return (
    <div className={cls} style={st}>
      <span className={s.csSlab}>{slabInner}</span>
      <span className={s.csBody}>{who}{v === "H" ? <b className={`${s.csSeat} ${s.csSeatDark}`}>P{i + 1}</b> : badge}</span>
      {v === "F" && <span className={s.csCap} />}
    </div>
  );
}
function CSGrid({ v, phone }: { v: string; phone: boolean }) {
  return (
    <>
      <div className={`${s.band} ${phone ? s.bandPhone : ""}`} />
      <div className={`${s.grid} ${phone ? s.gridPhone : ""}`}>{PLAYERS.map((p, i) => <div key={p.id} className={s.slot} style={{ "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties}><CS v={v} p={p} i={i} phone={phone} /></div>)}</div>
    </>
  );
}

function HB({ v, p, phone }: { v: string; p: P; phone: boolean }) {
  const color = wormColors[p.colorId];
  const you = p.id === "you";
  const t = p.rating != null ? insigniaForValue(p.rating) : null;
  const big = phone ? 30 : 40;
  const badge = <span className={s.ttBadge}><Badge p={p} size={big} /></span>;
  const tinted = "KLRSTUV".includes(v);
  const cls = `${s.hb} ${s["hb" + v]} ${tinted ? s.hbTint : ""} ${you && !tinted ? s.ttYou : ""} ${phone ? s.hbPhone : ""}`;
  const st = { "--worm": color } as React.CSSProperties;
  const disc = <span className={s.hbDisc} />;
  const who = <span className={s.csWho}><span className={s.csName} style={v === "I" ? { color } : undefined}>{p.name}</span><Flag code={p.flag} size={phone ? 16 : 20} /></span>;
  if (v === "F") return <div className={cls} style={st}>{who}{disc}{badge}</div>;
  if (v === "G") return <div className={cls} style={st}>{who}{badge}</div>;
  if (v === "J" || v === "K" || v === "L") return <div className={cls} style={st}>{disc}{who}<span className={s.csBlock}><Badge p={p} size={big} /></span></div>;
  const flagCol = <span className={s.hbFlagCol}><Flag code={p.flag} size={phone ? 18 : 24} /></span>;
  const nameOnly = <span className={s.csName}>{p.name}</span>;
  const block = <span className={s.csBlock}><Badge p={p} size={big} /></span>;
  const layout = ({ M: "after", N: "before", O: "block", P: "under", Q: "first", R: "after", S: "before", T: "block", U: "under", V: "first" } as Record<string, string>)[v];
  if (layout === "after") return <div className={cls} style={st}>{disc}{flagCol}{nameOnly}{block}</div>;
  if (layout === "before") return <div className={cls} style={st}>{disc}{nameOnly}{flagCol}{block}</div>;
  if (layout === "block") return <div className={cls} style={st}>{disc}{nameOnly}<span className={`${s.csBlock} ${s.hbBlockFlag}`}><Flag code={p.flag} size={phone ? 16 : 20} /><Badge p={p} size={phone ? 26 : 32} /></span></div>;
  if (layout === "under") return <div className={cls} style={st}>{disc}<span className={s.hbLines}>{nameOnly}<Flag code={p.flag} size={phone ? 16 : 20} /></span>{block}</div>;
  if (layout === "first") return <div className={cls} style={st}>{flagCol}{disc}{nameOnly}{block}</div>;
  return <div className={cls} style={st}>{v === "E" ? <span className={s.hbWorm}><i /><b /></span> : disc}{who}{v === "B" ? <span className={s.rank} style={t ? { color: t.tier.color } : undefined}><Badge p={p} size={big} /><span className={s.tier}>{t ? t.label : "Placements"}</span></span> : badge}</div>;
}
function HBGrid({ v, phone }: { v: string; phone: boolean }) {
  return (
    <>
      <div className={`${s.band} ${phone ? s.bandPhone : ""}`} />
      <div className={`${s.grid} ${phone ? s.gridPhone : ""}`}>{PLAYERS.map((p, i) => <div key={p.id} className={s.slot} style={{ "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties}><HB v={v} p={p} phone={phone} /></div>)}</div>
    </>
  );
}

export default function FfaBannerLab() {
  const [phone, setPhone] = useState(false);
  return (
    <div className={s.lab}>
      <div className={s.bar}>
        <span className={s.barLabel}>Device</span>
        <div className={s.seg}><button className={`${s.segBtn} ${!phone ? s.segOn : ""}`} onClick={() => setPhone(false)}>Desktop</button><button className={`${s.segBtn} ${phone ? s.segOn : ""}`} onClick={() => setPhone(true)}>Phone</button></div>
        <span className={s.hint}>you are Mathias (blue); the arena dimmed behind, as in the match</span>
      </div>
      <h1 className={s.h1}>Head block, varied</h1>
      {HEADS.map((d) => (
        <section key={d.id} className={s.row}>
          <h2 className={s.h2}><b>1{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          <div className={phone ? s.framePhone : s.frame}>
            <div className={s.arena} /><div className={s.dim} />
            <HBGrid v={d.id} phone={phone} />
          </div>
        </section>
      ))}
      <h1 className={s.h1}>Two-tone, varied</h1>
      {TWO_TONE.map((d) => (
        <section key={d.id} className={s.row}>
          <h2 className={s.h2}><b>6{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          <div className={phone ? s.framePhone : s.frame}>
            <div className={s.arena} /><div className={s.dim} />
            <TTGrid v={d.id} phone={phone} />
          </div>
        </section>
      ))}
      <h1 className={s.h1}>Colour slab, varied</h1>
      {SLAB.map((d) => (
        <section key={d.id} className={s.row}>
          <h2 className={s.h2}><b>2{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          <div className={phone ? s.framePhone : s.frame}>
            <div className={s.arena} /><div className={s.dim} />
            <CSGrid v={d.id} phone={phone} />
          </div>
        </section>
      ))}
      <h1 className={s.h1}>The ten</h1>
      {DESIGNS.map((d) => (
        <section key={d.id} className={s.row}>
          <h2 className={s.h2}><b>{d.id}</b> {d.name} <span>{d.blurb}</span></h2>
          <div className={phone ? s.framePhone : s.frame}>
            <div className={s.arena} /><div className={s.dim} />
            <Grid d={d.id} phone={phone} />
          </div>
        </section>
      ))}
    </div>
  );
}
